import isEmail from "validator/lib/isEmail";
import { ISelectedOptions } from "../../../types";
import { consoleLog } from "../../consoleLog/consoleLog";
import { isAllowHyphenAposEnabled, isAllowNumDotEnabled } from "../../../constant/featureSwitches";
import {
  getFirstLastNameSchema,
  getMiddleNameSchema,
  emailJoiSchema,
  isJoiValid,
} from "./joiSchemas";

/**
 * Validates the CHARACTER SET of a name field (first/middle/last) — used on
 * onChange so out-of-set characters can't be typed/pasted. Base set: letters +
 * spaces (internal spaces allowed; leading space stripped separately by
 * `blockLeadingSpace`). Digits `0-9` and dot `.` are allowed ONLY when the
 * `ALLOW_NUM_DOT` switch is on (`isAllowNumDotEnabled()`); hyphen `-` and
 * apostrophe `'` ONLY when `ALLOW_HYPHEN_APOS` is on (`isAllowHyphenAposEnabled()`).
 * Both default OFF → the default allowed set is letters + spaces only. Empty is
 * valid (so the field can be cleared).
 */
export function isValidNameInput(value: string): boolean {
  if (value === "") return true;
  // Build the allowed class from the two switches. `-` is kept LAST in the class
  // so it stays a literal hyphen (not a range).
  const numDot = isAllowNumDotEnabled() ? "0-9." : "";
  const hyphenApos = isAllowHyphenAposEnabled() ? "'-" : "";
  return new RegExp(`^[A-Za-z\\s${numDot}${hyphenApos}]+$`).test(value);
}

/**
 * Checks if a name has at least `min` alphabetic characters.
 */
export function hasMinAlphabets(value: string, min: number = 3): boolean {
  const alphaCount = (value.match(/[A-Za-z]/g) || []).length;
  return alphaCount >= min;
}

/**
 * Validity rule for a MANDATORY name field (First / Last): non-empty, at least
 * 2 characters, and the first character must be a LETTER (so a name can't start
 * with a digit/dot/space — catches e.g. "18moto" from paste/autofill). The
 * starts-with-letter check subsumes the old "≥1 alphabet" rule. Digits are
 * allowed AFTER the first letter (e.g. "MOTO17"); the char SET is enforced
 * separately at onChange via `isValidNameInput`.
 */
export function isValidMandatoryName(value: string): boolean {
  const v = (value ?? "").trim();
  // Joi safety-net: same rules encoded as a schema in ./joiSchemas.ts. Both
  // must pass — if either the regex checks OR joi ever drifts, the field is
  // treated as invalid.
  return (
    v.length >= 2 &&
    /^[A-Za-z]/.test(v) &&
    isJoiValid(getFirstLastNameSchema(), v)
  );
}

/**
 * Context-specific error message for a MANDATORY name field (First / Last).
 * Single source of truth for the copy — used by both the on-blur display
 * (NameForm / DisplayBasicInfo) and the Next-click block gates, so they never
 * disagree. Returns "" when the value is valid.
 *   - starts with a non-letter (e.g. "18moto") → mentions "must start with a letter"
 *   - otherwise (empty / single letter "M") → just the minimum-length rule
 */
export function getMandatoryNameErrorMessage(value: string, field: "first" | "last"): string {
  const v = (value ?? "").trim();
  if (v !== "" && !/^[A-Za-z]/.test(v)) {
    return `Enter a valid ${field} name (minimum 2 characters and must start with a letter).`;
  }
  if (v.length < 2) {
    return `Enter a valid ${field} name (minimum 2 characters).`;
  }
  // Character set (letters+spaces by default; digits/dot/hyphen/apos only when the
  // corresponding feature switch is on). Catches autofill/paste/legacy values like
  // "Moto18" that pass isValidMandatoryName (length + starts-with-letter only) but
  // still contain out-of-set characters — typing already blocks these via onChange.
  if (!isValidNameInput(v)) {
    return `Enter a valid ${field} name (letters only).`;
  }
  // Joi safety-net (final fallback): if the existing rules all pass but the
  // joi schema in ./joiSchemas.ts still rejects, return a generic message so
  // the red border never renders with an empty message underneath. In steady
  // state (rules agree) this branch never fires.
  if (!isJoiValid(getFirstLastNameSchema(), v)) {
    return `Enter a valid ${field} name.`;
  }
  return "";
}

/**
 * Validity rule for the MIDDLE name: OPTIONAL — empty is valid, and when filled it
 * only needs at least one alphabet (a single initial like "J" is allowed; NO
 * minimum-2 rule — middle is not a mandatory field). The First+Middle combined
 * LENGTH cap is enforced separately (getNameFieldLengthError / getMiddleMaxLength).
 */
export function isValidMiddleName(value: string): boolean {
  const v = (value ?? "").trim();
  // Joi safety-net: schema in ./joiSchemas.ts encodes the same "empty OR ≥1
  // alphabet + in-set chars + own-cap 26" rule. Both must pass. The DYNAMIC
  // First+Middle=28 combined budget is NOT enforced here — that stays owned by
  // getNameLengthError at both the display and gate layers.
  return (
    (v === "" || hasMinAlphabets(v, 1)) &&
    isJoiValid(getMiddleNameSchema(), v)
  );
}

/**
 * Context-specific error message for the MIDDLE name. Empty → "" (valid). Only
 * fires when a non-empty middle contains no alphabet at all (rare — the default
 * char-set already blocks digit/dot-only input). Single source: display + gates.
 */
export function getMiddleNameErrorMessage(value: string): string {
  const v = (value ?? "").trim();
  if (v === "") return "";
  // Piece-wise checks so the most-specific message wins (mirrors the mandatory
  // helper's structure). `isValidMiddleName` now folds in the joi safety-net,
  // so we can't call it as a single boolean here — otherwise a char-set
  // failure would produce the generic "Enter a valid middle name." message
  // instead of the specific "letters only" one.
  if (!hasMinAlphabets(v, 1)) return "Enter a valid middle name.";
  // Char-set (letters only, switch-aware) — catches an autofilled middle like "R18".
  if (!isValidNameInput(v)) return "Enter a valid middle name (letters only).";
  // Joi safety-net (final fallback): if the existing rules all pass but the
  // joi schema in ./joiSchemas.ts still rejects, return the generic message
  // so the red border never renders with an empty message underneath. In
  // steady state (rules agree) this branch never fires.
  if (!isJoiValid(getMiddleNameSchema(), v)) return "Enter a valid middle name.";
  return "";
}

/**
 * Space-joined display name for a traveler — includes the MIDDLE name only when
 * entered (e.g. "Moto Kings" or "Moto R Kings"). Trims each part and skips
 * empties. Used for the traveler card header + recent-traveler dropdown trigger.
 * Returns "" when nothing is entered (callers apply their own "New Traveler"
 * fallback / placeholder).
 */
export function getTravelerDisplayName(
  traveler: { firstName?: string; middleName?: string; lastName?: string } | null | undefined
): string {
  return [traveler?.firstName, traveler?.middleName, traveler?.lastName]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/**
 * Airline/GDS name-length caps. Per-field caps; PLUS a combined budget for the
 * "given name" group (First + Middle joined with a single space) so the Middle
 * field's effective max SHRINKS as First fills. Last name is INDEPENDENT.
 * Typing is NOT blocked at these caps — the user may exceed and an inline
 * "… too long (max N exceeded)" error shows ON TYPING (see getNameFieldLengthError);
 * the Next-click gate (getNameLengthError) still blocks SUBMISSION of an
 * over-length name so it can't reach the airline/prebook.
 */
export const NAME_LIMITS = { first: 28, middle: 26, last: 28, firstMiddleCombined: 28 } as const;

/**
 * Dynamic max length allowed for the MIDDLE field given the current First name:
 * the smaller of Middle's own cap (26) and the room left in the First+Middle
 * combined budget (28) after First + the single joining space. Floored at 0.
 * (First empty → no joining space → full 28 room, still capped at 26.)
 */
export function getMiddleMaxLength(firstName: string | null | undefined): number {
  const firstLen = (firstName ?? "").length;
  const roomInCombined = NAME_LIMITS.firstMiddleCombined - (firstLen > 0 ? firstLen + 1 : 0);
  return Math.max(0, Math.min(NAME_LIMITS.middle, roomInCombined));
}

/**
 * Per-field "… too long (max N exceeded)" message shown ON TYPING (real-time) the
 * moment a name field passes its max — First 28, Last 28, Middle = dynamic
 * getMiddleMaxLength(First). Returns "" when within limit. Typing is NOT blocked
 * (the user may exceed); this drives BOTH the inline on-typing error AND the
 * Next-click gate. Middle is reported only while First is within its OWN cap — if
 * First is over-length, First's error wins (fix First first) and the combined
 * math (which would otherwise go to 0/negative) is skipped.
 */
export function getNameFieldLengthError(
  field: "first" | "middle" | "last",
  traveler: { firstName?: string; middleName?: string; lastName?: string } | null | undefined
): string {
  const f = traveler?.firstName ?? "";
  const m = traveler?.middleName ?? "";
  const l = traveler?.lastName ?? "";
  if (field === "first") return f.length > NAME_LIMITS.first ? `First name too long (max ${NAME_LIMITS.first} exceeded)` : "";
  if (field === "last") return l.length > NAME_LIMITS.last ? `Last name too long (max ${NAME_LIMITS.last} exceeded)` : "";
  // middle — skip while First is over its own cap (First's error takes priority)
  if (f.length > NAME_LIMITS.first) return "";
  const mMax = getMiddleMaxLength(f);
  return m.length > mMax ? `First & Middle name too long (max ${NAME_LIMITS.firstMiddleCombined} exceeded)` : "";
}

/**
 * Which name field (if any) currently overflows + message — the Next-click gate
 * backstop. Since typing is no longer blocked, this is what stops an over-length
 * name from reaching prebook. Checks First, then Last, then Middle (dynamic).
 * Middle is skipped when it's disabled (First ate the whole budget) — it can't be
 * edited in that state, and First's own error is already surfaced.
 */
export function getNameLengthError(
  traveler: { firstName?: string; middleName?: string; lastName?: string } | null | undefined
): { field: "first" | "middle" | "last"; message: string } | null {
  const fMsg = getNameFieldLengthError("first", traveler);
  if (fMsg) return { field: "first", message: fMsg };
  const lMsg = getNameFieldLengthError("last", traveler);
  if (lMsg) return { field: "last", message: lMsg };
  if (!isMiddleNameDisabledByFirst(traveler)) {
    const mMsg = getNameFieldLengthError("middle", traveler);
    if (mMsg) return { field: "middle", message: mMsg };
  }
  return null;
}

/**
 * True when the MIDDLE-name input should be DISABLED because the First name has
 * consumed so much of the shared First+Middle budget (28) that no VALID middle
 * name (min 2 chars + the joining space) can fit. Room for middle content =
 * `firstMiddleCombined - firstLen - 1` (the single space); disable once that is
 * `< 2`, i.e. First length ≥ 26. Only disables while Middle is CURRENTLY EMPTY,
 * so a non-empty middle is never trapped (stays editable/clearable). Used to set
 * the `disabled` prop on the middle CustomInput in NameForm + DisplayBasicInfo.
 */
export function isMiddleNameDisabledByFirst(
  traveler: { firstName?: string; middleName?: string } | null | undefined
): boolean {
  const firstLen = (traveler?.firstName ?? "").length;
  const middleLen = (traveler?.middleName ?? "").length;
  if (middleLen > 0) return false; // never disable while it holds content
  return NAME_LIMITS.firstMiddleCombined - firstLen - 1 < 2;
}

/**
 * Validity rule for an OPTIONAL name field (Middle name, and the Activity
 * name-type booking questions). Empty is valid; if filled it must contain at
 * least one alphabet (so "1111" is rejected but a single-initial "J" is fine —
 * no minimum length). See JIRA SMA-777.
 */
export function isValidOptionalName(value: string): boolean {
  const v = value ?? "";
  return v.trim() === "" || hasMinAlphabets(v, 1);
}

/**
 * Removes leading spaces only — used on name onChange so a space can never be
 * the first character, while spaces in the middle ("A K") and a trailing space
 * (while still typing) are preserved.
 */
export function blockLeadingSpace(value: string): string {
  return value.replace(/^\s+/, "");
}

/**
 * Trims trailing spaces only — used on name onBlur so "A K " becomes "A K" when
 * the user leaves the field. Leading spaces are already prevented on change.
 */
export function trimTrailingSpace(value: string): string {
  return value.replace(/\s+$/, "");
}

/**
 * Removes every whitespace character — used on email onChange so no space can
 * be typed/pasted anywhere in the address.
 */
export function removeAllSpaces(value: string): string {
  return value.replace(/\s+/g, "");
}

/**
 * Keeps only RFC-valid *unquoted* email characters — used on email onChange so
 * the input follows RFC without allowing the quoted-only forms. Allowed set:
 * letters, digits, `@`, and the full RFC unquoted local-part atext
 * `. ! # $ % & ' * + / = ? ^ _ \` { | } ~ -`. Stripped at keystroke/paste:
 * whitespace and the quoted-only / structural chars `" ( ) , : ; < > \ [ ]`
 * (rare and unsupported by most providers) — the same chars `emailRegex`
 * forbids in the local part. This only restricts the character *set*;
 * structural validity (a proper `@`, domain, TLD, no leading/consecutive dots,
 * letters/digits/hyphen-only domain) is still enforced on blur via `emailRegex`.
 */
export function sanitizeEmailInput(value: string): string {
  return value.replace(/[^A-Za-z0-9.!#$%&'*+\/=?^_`{|}~@-]/g, "");
}

/**
 * Traveler-form email format check — backed by the `validator` library's
 * `isEmail` (RFC). This REPLACES the custom `emailRegex` in the traveler-booking
 * flow (per product decision Jul 2026); `emailRegex` in `src/constant/common.ts`
 * is intentionally still used by login/signup/reset/share (out of scope here).
 * Coerces to a string (validator throws on non-string) + defensive trim.
 */
export function isValidEmail(value: string): boolean {
  const trimmed = String(value ?? "").trim();
  // Joi safety-net: schema in ./joiSchemas.ts encodes the same RFC-lite +
  // max-92 rule. Both `validator/isEmail` AND joi must accept the address.
  return isEmail(trimmed) && isJoiValid(emailJoiSchema, trimmed);
}

/**
 * Airline/GDS email-length cap. Like the name-length caps, typing is NOT hard-blocked
 * at this value — the user may exceed it and an inline "Email Address is too long
 * (Max N characters allowed)" error shows ON TYPING (real-time, no touched/flag gate);
 * the Next-click gates still BLOCK submission of an over-length email. (A separate >100
 * paste-sanity ceiling lives in the email onChange handlers.) Single source of truth for
 * both the display AND the gates so block == display. Returns "" when within the cap
 * (empty included).
 */
export const EMAIL_MAX_LENGTH = 92;
export function getEmailLengthError(email: string | null | undefined): string {
  return (email ?? "").length > EMAIL_MAX_LENGTH
    ? `Email Address is too long (Max ${EMAIL_MAX_LENGTH} characters allowed)`
    : "";
}

const MONTH_NAMES_LOWER = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/**
 * Resolves a traveler-form month field to a 1–12 number, tolerant of every
 * format the month can actually be stored in:
 *   - full English label   → "August"
 *   - 3-char abbreviation  → "Aug" / "AUG"
 *   - already numeric      → "08" / "8"
 * Returns NaN when it can't be resolved.
 *
 * Why this exists: the dropdown emits the full label on mouse-click, but the
 * keyboard/type-ahead path and recent-traveler autofill can leave a 3-char or
 * numeric value. The old date checks built `new Date(\`${year}-${options.find(o
 * => o.label === month)?.value}-${day}\`)` — when `month` wasn't the exact full
 * label, `.value` was `undefined`, the string became "YYYY-undefined-DD", and
 * `new Date(...)` returned an Invalid Date (NaN). Any `<`/`<=`/`>` comparison
 * against NaN is false, so the validation silently passed.
 */
export function resolveMonthNumber(month: string): number {
  if (!month && month !== "0") return NaN;
  const m = String(month).trim().toLowerCase();
  if (m === "") return NaN;
  if (/^\d{1,2}$/.test(m)) {
    const n = Number(m);
    return n >= 1 && n <= 12 ? n : NaN;
  }
  const key = m.substring(0, 3);
  const idx = MONTH_NAMES_LOWER.findIndex((name) => name === m || name.substring(0, 3) === key);
  return idx >= 0 ? idx + 1 : NaN;
}

/**
 * Builds a chronologically-comparable YYYYMMDD integer from separate
 * year/month/day traveler-form fields. Month is resolved via
 * `resolveMonthNumber` (format-tolerant). Returns NaN if any part is
 * missing/unparseable — callers should treat NaN as "can't compare" and skip
 * (never false-positive). Avoids `new Date(string)` parsing entirely.
 */
export function travelerDatePartsToNumber(year: string, month: string, day: string): number {
  const y = Number(String(year).trim());
  const mo = resolveMonthNumber(month);
  const d = Number(String(day).trim());
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return NaN;
  if (String(year).trim().length !== 4 || d < 1 || d > 31) return NaN;
  return y * 10000 + mo * 100 + d;
}

/**
 * Like `travelerDatePartsToNumber` but returns epoch milliseconds (for real
 * day-difference math, e.g. the passport "expiry ≥ issue + 6 months" check).
 * NaN if unparseable. Built via `new Date(y, mo-1, d)` (local midnight) — never
 * `new Date(string)`, so it doesn't depend on the month being the exact label.
 */
export function travelerDatePartsToTime(year: string, month: string, day: string): number {
  const y = Number(String(year).trim());
  const mo = resolveMonthNumber(month);
  const d = Number(String(day).trim());
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return NaN;
  return new Date(y, mo - 1, d).getTime();
}

/** Date object → comparable YYYYMMDD integer (date-only). NaN for an invalid Date. */
export function dateToYyyymmdd(date: Date): number {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return NaN;
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

/**
 * Max valid day for a traveler-form month field.
 *   `month` — full label / 3-char / numeric (resolved via `resolveMonthNumber`),
 *             so it works for both the active flow ("February") and Activities ("02").
 *   `year`  — optional 4-digit string. When present it makes February leap-aware
 *             (2000-02 → 29, 2007-02 → 28); when the year isn't entered yet,
 *             February falls back to 29 (leap-safe, so a Feb-29 birthday can be typed).
 * Returns 31 when the month can't be resolved (i.e. no restriction — same as before).
 * Uses the standard `new Date(year, month, 0).getDate()` last-day-of-month trick.
 */
export function getMaxDayForTravelerMonth(month: string, year?: string): number {
  const mo = resolveMonthNumber(month);
  if (!Number.isFinite(mo)) return 31;
  const yStr = String(year ?? "").trim();
  const y = yStr.length === 4 ? Number(yStr) : NaN;
  if (mo === 2 && !Number.isFinite(y)) return 29;
  return new Date(Number.isFinite(y) ? y : 2000, mo, 0).getDate();
}

/**
 * Robust "is this a real calendar date" check for a traveler-form date built
 * from separate year/month/day fields (passport issue/expiry). Catches an
 * out-of-range day (65 → NaN), a bad month/year, AND a month-specific overflow
 * (Apr 31, Feb 29 in a non-leap year). Format-tolerant month (full label /
 * 3-char / numeric) via `resolveMonthNumber` — replaces the fragile
 * `new Date(\`${y}-${options.find(o=>o.label===month)?.value}-${d}\`)` checks
 * that silently no-op'd when the month wasn't the exact full label.
 * Returns false when any part is empty/unparseable.
 */
export function isValidTravelerDate(year: string, month: string, day: string): boolean {
  if (!Number.isFinite(travelerDatePartsToNumber(year, month, day))) return false;
  return Number(String(day).trim()) <= getMaxDayForTravelerMonth(month, year);
}

/**
 * Passport-expiry ADVISORY (non-blocking). True when the expiry is a real,
 * complete date that is AFTER the travel date but within `monthsThreshold`
 * months of it — i.e. the passport is valid for the trip yet expires soon after
 * (many countries require 3–6 months validity beyond travel). Window is
 * (travelDate, travelDate + N months]; expiry ≤ travelDate is already the
 * blocking "expiry must be after travel" error, so this only flags valid-but-soon.
 * Date-only YYYYMMDD compare — same handling as the existing expiry-vs-travel gate.
 * Returns false when travelDate is missing/unparseable or expiry is incomplete/invalid.
 */
export function isPassportExpiringSoon(
  year: string,
  month: string,
  day: string,
  travelDate: string | undefined,
  monthsThreshold: number = 3
): boolean {
  if (!travelDate) return false;
  const travel = new Date(travelDate);
  if (Number.isNaN(travel.getTime())) return false;
  if (!isValidTravelerDate(year, month, day)) return false;
  const expiryNum = travelerDatePartsToNumber(year, month, day);
  const travelNum = dateToYyyymmdd(travel);
  const threshold = new Date(travel);
  threshold.setMonth(threshold.getMonth() + monthsThreshold);
  const thresholdNum = dateToYyyymmdd(threshold);
  return (
    Number.isFinite(expiryNum) &&
    Number.isFinite(travelNum) &&
    Number.isFinite(thresholdNum) &&
    expiryNum > travelNum &&
    expiryNum <= thresholdNum
  );
}

/**
 * The trip's LAST-leg travel date — the single "age/travel reference date" for the DOB
 * age band. Returns the date STRING (callers feed it to `new Date(...)`), or `undefined`.
 *
 * - **Multi-leg array (`length > 1`) = Shop Travel** (multi-city, or round-trip stored as
 *   2 legs): consider **`departureDate` ONLY**. Every real flight is a leg's departure
 *   (the round-trip return leg included), so the departure MAX already gives the true last
 *   leg. The per-leg `returnDate` here is a SYNTHETIC length-of-stay artifact
 *   (`departure + noOfDays − 1`, default +2 days — multiCityInputFields.tsx) that must NOT
 *   count as a flight date, else it OVER-AGES the traveler (e.g. last leg 8 Oct 2026 + 2d
 *   = 10 Oct 2026 flips a just-under-2 infant into the Child band). Ignoring returnDate on
 *   ALL multi-leg entries is robust even when the FIRST leg is deleted (the promoted leg's
 *   synthetic returnDate is still ignored).
 * - **Single entry (`length === 1`) = collapsed CHAT, or Shop one-way / single-entry
 *   round-trip**: consider **both** `departureDate` and `returnDate`. Chat stashes the real
 *   trip-end (round-trip return / multi-city last-leg departure) into `airPort[0].returnDate`
 *   (`primeChatFlightBookingState`); a Shop 1-entry round-trip's `returnDate` is the real
 *   return. (This also means Shop round-trip is correct whether it's 1 entry or 2.)
 *
 * KNOWN NARROW EDGES (rare, pre-existing — see traveler-forms.md; a fully-airtight fix needs
 * a dedicated last-leg field + a chat-priming `slice(1)` cleanup): (a) a Shop multi-city trip
 * deleted down to a SINGLE synthetic leg → its `returnDate` (dep+2) would be read; (b) a CHAT
 * booking whose `airPort` still carries stale `slice(1)` entries from a prior search
 * (`length > 1`) → the departure-only path ignores the returnDate stash.
 */
export function getAgeReferenceDate(
  bookingDetails: { airPort?: Array<{ departureDate?: string; returnDate?: string }> } | null | undefined
): string | undefined {
  const legs = bookingDetails?.airPort;
  if (!Array.isArray(legs) || legs.length === 0) return undefined;
  // Single entry → chat stash / real round-trip return lives on returnDate (trust it).
  // Multi-leg → Shop: every returnDate is a synthetic stay artifact → departureDate ONLY.
  const singleEntry = legs.length === 1;
  let bestStr: string | undefined;
  let bestTime = -Infinity;
  for (const leg of legs) {
    const candidates = singleEntry ? [leg?.departureDate, leg?.returnDate] : [leg?.departureDate];
    for (const d of candidates) {
      if (!d) continue;
      const t = new Date(d).getTime();
      if (Number.isFinite(t) && t > bestTime) {
        bestTime = t;
        bestStr = d;
      }
    }
  }
  return bestStr ?? legs.at(-1)?.departureDate ?? undefined;
}

// ---------------------------------------------------------------------------
// Passenger-type–aware Date-of-Birth validation (flight traveler form)
// ---------------------------------------------------------------------------
// Single source of truth for DOB validity: both the Next-click blocking gates
// (travelerHotelContextUtils / TravelerCrumbsDetailsContext) AND the on-blur
// display (TravelerForm) call `validateTravelerDob` with the SAME travel date,
// so the block and the message can never disagree.
//
// Age is evaluated at the TRAVEL date (return date for round trips, last-city
// date for multi-city, departure for one-way — the caller passes
// `getAgeReferenceDate(bookingDetails)`, which resolves the true last leg in BOTH
// Shop Travel and chat). ALL bands — infant, child, adult-min AND the adult >120
// upper cap — are evaluated at that last-leg travel date (per product: "these
// three count from the last leg").

/** Adult may not be older than this many years (from today). */
const ADULT_MAX_AGE = 120;
/** Flight: adult >= 12, child [2, 12), infant < 2. */
const FLIGHT_ADULT_MIN_AGE = 12;
const FLIGHT_CHILD_MIN_AGE = 2;
const FLIGHT_CHILD_MAX_AGE = 12;
const INFANT_MAX_AGE = 2;
/** Hotel/Stay: adult >= 18, child < 18 (no infant band). */
const HOTEL_ADULT_MIN_AGE = 18;
const HOTEL_CHILD_MAX_AGE = 18;

export const DOB_ERROR_MESSAGES = {
  missing: "Please enter the passenger's Date of Birth.",
  month: "Please select a valid birth month.",
  // Out-of-range day (e.g. 65) → same copy as invalidCombination per product,
  // so every "wrong date" case reads "Please enter a valid Date of Birth".
  date: "Please enter a valid Date of Birth.",
  year: "Please enter a valid birth year.",
  invalidCombination: "Please enter a valid Date of Birth.",
  future: "Date of Birth cannot be a future date.",
  adult:
    "Age doesn't match the Adult passenger type (12+ yrs). Update the Date of Birth or change the passenger type.",
  child:
    "Age doesn't match the Child passenger type (2–12 yrs). Update the Date of Birth or change the passenger type.",
  infant:
    "Age doesn't match the Infant  passenger type (0–2 yrs). Update the Date of Birth or change the passenger type.",
};

/** Which DOB sub-field an error anchors to (drives the scroll target). "group" = the whole DOB row. */
export type DobValidationField = "month" | "date" | "year" | "group";
export interface DobValidationError {
  field: DobValidationField;
  message: string;
}

/** Whole years between two YYYYMMDD integers (birthday-aware; MMDD is embedded). */
function ageInYearsFromYyyymmdd(fromYyyymmdd: number, dobYyyymmdd: number): number {
  return Math.floor((fromYyyymmdd - dobYyyymmdd) / 10000);
}

/**
 * Validates a traveler's Date of Birth (three separate parts) independently per
 * field, then as a whole against the passenger slot's age band. Returns the
 * FIRST failing check (field + specific message), or null when the DOB is valid.
 *
 *  - `paxType`        — "ADT" | "CHD" | "INF" (slot type). Age band is only
 *                        checked when this is set.
 *  - `isFlightBooking`— picks flight bands (infant/child/adult @ 2/12) vs hotel
 *                        bands (child/adult @ 18).
 *  - `travelingDate`  — the trip's last-leg date (return / last-city / departure)
 *                        used for ALL bands (infant / child / adult-min AND the
 *                        adult >120 cap). Falls back to today when absent/unparseable.
 */
export function validateTravelerDob(
  month: string,
  day: string,
  year: string,
  paxType?: string,
  isFlightBooking: boolean = true,
  travelingDate?: string
): DobValidationError | null {
  const m = (month ?? "").trim();
  const d = (day ?? "").trim();
  const y = (year ?? "").trim();

  // 1) Nothing entered at all.
  if (!m && !d && !y) return { field: "group", message: DOB_ERROR_MESSAGES.missing };

  // 2) Month — must resolve to 1–12.
  const monthNum = resolveMonthNumber(m);
  if (!m || !Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
    return { field: "month", message: DOB_ERROR_MESSAGES.month };
  }

  // 3) Date — numeric 1–31.
  const dayNum = Number(d);
  if (!d || !/^\d{1,2}$/.test(d) || dayNum < 1 || dayNum > 31) {
    return { field: "date", message: DOB_ERROR_MESSAGES.date };
  }

  // 4) Year — exactly 4 digits.
  if (!y || !/^\d{4}$/.test(y)) {
    return { field: "year", message: DOB_ERROR_MESSAGES.year };
  }

  // 5) Invalid combination — e.g. Feb 31, Apr 31, Feb 29 in a non-leap year.
  if (dayNum > getMaxDayForTravelerMonth(m, y)) {
    return { field: "group", message: DOB_ERROR_MESSAGES.invalidCombination };
  }

  const dobNum = travelerDatePartsToNumber(y, m, d);
  if (!Number.isFinite(dobNum)) {
    return { field: "group", message: DOB_ERROR_MESSAGES.invalidCombination };
  }

  // 6) Future date.
  const todayNum = dateToYyyymmdd(new Date());
  if (dobNum > todayNum) {
    return { field: "group", message: DOB_ERROR_MESSAGES.future };
  }

  // 7) Passenger-type age band (only when the slot type is known).
  if (paxType) {
    const parsedTravel = travelingDate ? new Date(travelingDate) : new Date();
    const travelNum = Number.isNaN(parsedTravel.getTime()) ? todayNum : dateToYyyymmdd(parsedTravel);
    const ageAtTravel = ageInYearsFromYyyymmdd(travelNum, dobNum);

    if (paxType === "ADT") {
      const adultMin = isFlightBooking ? FLIGHT_ADULT_MIN_AGE : HOTEL_ADULT_MIN_AGE;
      if (ageAtTravel < adultMin || ageAtTravel > ADULT_MAX_AGE) {
        return { field: "group", message: DOB_ERROR_MESSAGES.adult };
      }
    } else if (paxType === "CHD") {
      const childOk = isFlightBooking
        ? ageAtTravel >= FLIGHT_CHILD_MIN_AGE && ageAtTravel < FLIGHT_CHILD_MAX_AGE
        : ageAtTravel >= 0 && ageAtTravel < HOTEL_CHILD_MAX_AGE;
      if (!childOk) return { field: "group", message: DOB_ERROR_MESSAGES.child };
    } else if (paxType === "INF") {
      if (!(ageAtTravel >= 0 && ageAtTravel < INFANT_MAX_AGE)) {
        return { field: "group", message: DOB_ERROR_MESSAGES.infant };
      }
    }
  }

  return null;
}

export { };
interface iTravelerInfoArrayDataRef {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirthMonth: string;
  dateOfBirth: string;
  dateOfYear: string;
  gender: string;
  email: string;
  documentType: string;
  documentNumber: string;
  issuingCountry: string;
  nationality: string;
  docIssueMonth: string;
  docIssueDate: string;
  docIssueYear: string;
  docExpiryMonth: string;
  docExpiryDate: string;
  docExpiryYear: string;
  countryCode: string;
  phoneNumber: string;
  programName: string;
  frequentFlyerNumber: string;
  passportNumber: string;
  mealPref: string;
  selectedOptions: Record<string, unknown>; // Assuming it's an object with dynamic keys
  requiredDataFilled: boolean;
}

export function checkIfOneTravelerFormIsComplete(
  formDataArr: any,
  setFormDataArr: any,
  selectedFlightOptions: ISelectedOptions,
  internationalFlight: boolean
) {
  const dupFormDataArr = JSON.parse(JSON.stringify(formDataArr));
  const modFormArr = dupFormDataArr?.map((ele: iTravelerInfoArrayDataRef, index: number) => {
    let requiredDataFilled = false;
    if (selectedFlightOptions?.flights) {
      if (
        ele?.firstName?.trim() !== "" &&
        ele?.lastName?.trim() !== "" &&
        ele?.dateOfYear?.trim() !== "" &&
        ele?.dateOfBirthMonth?.trim() !== "" &&
        ele?.dateOfBirth?.trim() !== "" &&
        ele?.gender?.trim() !== "" &&
        (ele?.email?.trim() !== "" || index > 0) &&
        (ele?.phoneNumber?.trim() !== "" || index > 0) &&
        ele?.documentType?.trim() !== "" &&
        ele?.documentNumber?.trim() !== "" &&
        ele?.issuingCountry?.trim() !== "" &&
        ele?.nationality?.trim() !== "" &&
        ele?.docIssueYear?.trim() !== "" &&
        ele?.docIssueMonth?.trim() !== "" &&
        ele?.docIssueDate?.trim() !== "" &&
        ele?.docExpiryYear?.trim() !== "" &&
        ele?.docExpiryMonth?.trim() !== "" &&
        ele?.docExpiryDate?.trim() !== ""
      ) {
        requiredDataFilled = true;
      } else if (!internationalFlight) {
        if (
          ele?.firstName?.trim() !== "" &&
          ele?.lastName?.trim() !== "" &&
          ele?.dateOfYear?.trim() !== "" &&
          ele?.dateOfBirthMonth?.trim() !== "" &&
          ele?.dateOfBirth?.trim() !== "" &&
          ele?.gender?.trim() !== "" &&
          (ele?.email?.trim() !== "" || index > 0) &&
          (ele?.phoneNumber?.trim() !== "" || index > 0)
          // ele?.documentType !== "" &&
          // ele?.documentNumber !== ""
          // ele?.issuingCountry !== "" &&
          // ele?.nationality !== ""
        )
          requiredDataFilled = true;
      }
    } else {
      if (
        ele?.firstName?.trim() !== "" &&
        ele?.lastName?.trim() !== "" &&
        ele?.gender?.trim() !== "" &&
        (ele?.email?.trim() !== "" || index > 0) &&
        // ele?.countryCode?.trim() !== "" &&
        (ele?.phoneNumber?.trim() !== "" || index > 0)
      ) {
        requiredDataFilled = true;
      }
    }
    consoleLog(["HERE"])
    return {
      ...ele,
      requiredDataFilled: requiredDataFilled,
      collapse: true
    };
  });
  setFormDataArr(modFormArr);
}
